import { PANEL_API_INTERVIEW_PARAM } from "@/components/outreach/panel/api/participantUrl";
import type { RecruitmentDashboardMetadata } from "@/components/responses/types";
import { notNull } from "@/utils/helpers";

export const EXTERNAL_PANEL_URL_PARAM_NAME = "src";

export type PanelInputField = {
  title: string;
  id: string;
  comment?: string;
  longDescription?: string;
  required?: boolean;
};

// Canonical vendor keys for external panels (stable identity, not a URL token).
// Used by product/config and future audience-plan mapping; distinct from `src`.
export type ExternalPanelKey =
  | "office_hours"
  | "third_bridge"
  | "prosapient"
  | "dialectica"
  | "glg"
  | "test"
  | "true_north_insights"
  | "norstat"
  | "sermo"
  | "monitas"
  | "newtonx"
  | "emporia"
  | "gmo"
  | "synquery"
  | "grayscale"
  | "catalyse"
  | "exact_insight"
  | "octopus"
  | "dynata"
  | "research_network"
  | "m3"
  | "konovo"
  | "market_xcel"
  | "embrain"
  | "paas"
  | "woozle"
  | "thinknow"
  | "paloma"
  | "equilibrium"
  | "newfield";

export type ExternalPanel = {
  // Canonical vendor identity (e.g. "office_hours"). Stable; not a URL token.
  key: ExternalPanelKey;
  name: string;
  // URL/attribution token that rides in the study link (?src=oh). Short and
  // sometimes historically overloaded; distinct from `key`.
  src: string;
  emailDomain: string;
  // Other domains the vendor bills from, when that is not emailDomain.
  billingEmailDomains?: string[];
  inputFields?: PanelInputField[];
  extraUrlParams?: (
    inputParams: Record<string, string>
  ) => Record<string, string>;
  paramsToAddToTable?: string[];
  participantIdParam?: string;
  superAdminOnly?: boolean;
  prefilledEmail?: string;
  // false retires the vendor: no new dashboards, existing ones keep resolving.
  isCreatable?: boolean;
  // Custom-link dashboards for this vendor offer the reject reasons download unless a super-admin turns it off.
  isRejectReasonsExportOnByDefault?: boolean;
};

// For panels without preset redirect endpoints: users paste the redirect
// links the panel sent them, forwarded as redirectUrl/screenoutUrl.
const COMPLETION_REDIRECT_FIELD_ID = "completionRedirectUrl";
const SCREEN_OUT_REDIRECT_FIELD_ID = "screenOutRedirectUrl";

const GENERIC_REDIRECT_INPUT_FIELDS: PanelInputField[] = [
  {
    title: "Completion Redirect URL",
    id: COMPLETION_REDIRECT_FIELD_ID,
    comment: "(optional)",
    longDescription:
      "Paste the completion redirect link the panel sent you. Respondents who finish the study are sent there.",
  },
  {
    title: "Screen-out Redirect URL",
    id: SCREEN_OUT_REDIRECT_FIELD_ID,
    comment: "(optional)",
    longDescription:
      "Paste the screen-out redirect link the panel sent you. Respondents who do not qualify are sent there.",
  },
];

const genericRedirectExtraUrlParams = (
  inputs: Record<string, string>
): Record<string, string> => {
  const completionUrl = inputs[COMPLETION_REDIRECT_FIELD_ID]?.trim();
  const screenOutUrl = inputs[SCREEN_OUT_REDIRECT_FIELD_ID]?.trim();
  return {
    ...(completionUrl ? { redirectUrl: completionUrl } : {}),
    ...(screenOutUrl ? { screenoutUrl: screenOutUrl } : {}),
  };
};

export const EXTERNAL_PANELS: ExternalPanel[] = [
  {
    key: "office_hours",
    name: "Office Hours",
    src: "oh",
    emailDomain: "officehours.com",
    isRejectReasonsExportOnByDefault: true,
    extraUrlParams: () => ({
      redirectUrl:
        "https://api.officehours.com/api/v2/survey-interactions/redirect?respondentId={{iid}}&result=qualified",
      screenoutUrl:
        "https://api.officehours.com/api/v2/survey-interactions/redirect?respondentId={{iid}}&result=terminated",
    }),
    participantIdParam: "iid",
    prefilledEmail: "surveys@officehours.com",
  },
  {
    key: "third_bridge",
    name: "Third Bridge",
    src: "tb",
    emailDomain: "thirdbridge.com",
    extraUrlParams: genericRedirectExtraUrlParams,
    participantIdParam: "eUuid",
    inputFields: GENERIC_REDIRECT_INPUT_FIELDS,
    prefilledEmail: "listenlabs@thirdbridge.com",
    isCreatable: false,
  },
  {
    key: "prosapient",
    name: "proSapient",
    src: "ps",
    emailDomain: "prosapient.com",
    extraUrlParams: () => ({
      redirectUrl: "https://survey-panels.prosapient.com/survey/pilot/complete",
      screenoutUrl:
        "https://survey-panels.prosapient.com/survey/pilot/screen_out",
    }),
    participantIdParam: "iid",
    prefilledEmail: "airesearch@prosapient.com",
  },
  {
    key: "dialectica",
    name: "Dialectica",
    src: "d",
    emailDomain: "dialecticanet.com",
    extraUrlParams: genericRedirectExtraUrlParams,
    participantIdParam: "iid",
    inputFields: GENERIC_REDIRECT_INPUT_FIELDS,
    prefilledEmail: "aiinterviews@dialecticanet.com",
  },
  {
    key: "glg",
    name: "GLG",
    src: "gl",
    emailDomain: "glgroup.com",
    extraUrlParams: (inputs): Record<string, string> => {
      const surveyCode = inputs.surveyCode;
      const studyId = inputs.studyId;
      if (!surveyCode || !studyId) return {};
      return {
        redirectUrl: `https://members.glgresearch.com/survey-accept/surveyoutcome/${surveyCode}/${studyId}/gc?rid={{rid}}`,
        screenoutUrl: `https://members.glgresearch.com/survey-accept/surveyoutcome/${surveyCode}/${studyId}/so?rid={{rid}}`,
      };
    },
    participantIdParam: "rid",
    inputFields: [
      {
        title: "Survey Code",
        id: "surveyCode",
        longDescription:
          "To include redirects in the study link, enter your Survey Code and Study ID — they'll populate automatically. Redirects are optional.",
        comment: "(optional)",
      },
      { title: "Study ID", id: "studyId", comment: "(optional)" },
    ],
  },
  {
    key: "test",
    name: "test",
    src: "test",
    emailDomain: "listenlabs.ai",
    participantIdParam: "iid",
    superAdminOnly: true,
    extraUrlParams: (inputs): Record<string, string> => {
      const projectId = inputs.dynamicVariable;
      if (!projectId) return {};
      return {
        redirectUrl: `https://sw2.decipherinc.com/survey/selfserve/31c4/${projectId}?list=3&ID={{iid}}&s=1`,
        screenoutUrl: `https://sw2.decipherinc.com/survey/selfserve/31c4/${projectId}?list=3&ID={{iid}}&s=2`,
      };
    },
    inputFields: [{ title: "Dynamic Variable", id: "dynamicVariable" }],
  },
  {
    key: "true_north_insights",
    name: "True North Insights",
    src: "c",
    emailDomain: "truenorthinsights.com",
    extraUrlParams: (inputs): Record<string, string> => {
      const dynamicVariable = inputs.dynamicVariable;
      if (!dynamicVariable) return {};
      return {
        redirectUrl: `https://sw2.decipherinc.com/survey/selfserve/31c4/${dynamicVariable}?list=3&ID={{iid}}&s=1`,
        screenoutUrl: `https://sw2.decipherinc.com/survey/selfserve/31c4/${dynamicVariable}?list=3&ID={{iid}}&s=2`,
      };
    },
    participantIdParam: "iid",
    inputFields: [{ title: "Dynamic Variable", id: "dynamicVariable" }],
    prefilledEmail: "simon.wang@truenorthinsights.com",
    isRejectReasonsExportOnByDefault: true,
  },
  {
    key: "norstat",
    name: "Norstat",
    src: "ns",
    emailDomain: "norstatgroup.com",
    extraUrlParams: () => ({
      redirectUrl: "https://webs.norstatsurveys.com/z/Complete",
      screenoutUrl: "https://webs.norstatsurveys.com/z/Screened",
    }),
    participantIdParam: "iid",
    prefilledEmail: "janis.romanovs@norstatgroup.com",
  },
  {
    key: "sermo",
    name: "Sermo",
    src: "s",
    emailDomain: "sermo.com",
    extraUrlParams: (inputs): Record<string, string> => {
      const dynamicVariable = inputs.dynamicVariable;
      if (!dynamicVariable) return {};
      return {
        redirectUrl: `https://rtp.sermo.com/lb/?s=${dynamicVariable}&p={{iid}}&o=C1`,
        screenoutUrl: `https://rtp.sermo.com/lb/?s=${dynamicVariable}&p={{iid}}&o=SO`,
      };
    },
    participantIdParam: "iid",
    inputFields: [
      {
        title: "Sermo Project Number",
        id: "dynamicVariable",
        comment: "(Required)",
        required: true,
        longDescription:
          "Enter your dynamic variable (typically a 5-digit code found in redirect parameters) to populate redirects.",
      },
    ],
    prefilledEmail: "listenlabs@sermo.com",
  },
  {
    key: "monitas",
    name: "Monitas",
    src: "m",
    emailDomain: "monitas.co.jp",
    extraUrlParams: () => ({
      redirectUrl:
        "https://survey.super-colony.com/compPage/?survey_id={{survey_id}}&monitors_id={{monitors_id}}&status=1",
      screenoutUrl:
        "https://survey.super-colony.com/compPage/?survey_id={{survey_id}}&monitors_id={{monitors_id}}&status=2",
    }),
    participantIdParam: "iid",
    paramsToAddToTable: ["survey_id", "monitors_id"],
  },
  {
    key: "newtonx",
    name: "NewtonX",
    src: "nx",
    emailDomain: "newtonx.com",
    extraUrlParams: (inputs): Record<string, string> => {
      const projectCode = inputs.projectCode?.trim();
      if (!projectCode) return {};
      const project = encodeURIComponent(projectCode);
      return {
        redirectUrl: `https://newtonx.decipherinc.com/survey/selfserve/c84/${project}?state=\${state}&rewar=c`,
        screenoutUrl: `https://newtonx.decipherinc.com/survey/selfserve/c84/${project}?state=\${state}&rewar=t`,
      };
    },
    participantIdParam: "iid",
    inputFields: [
      {
        title: "NewtonX Project Code",
        id: "projectCode",
        comment: "(Required)",
        required: true,
        longDescription:
          "Enter the project code provided by NewtonX for this study, e.g. 2609225.",
      },
    ],
  },
  {
    key: "emporia",
    name: "Emporia",
    src: "em",
    emailDomain: "emporiaapp.com",
    extraUrlParams: () => ({
      redirectUrl:
        "https://app.emporiaresearch.com/survey/interviewcomplete?uid={{uid}}",
      screenoutUrl:
        "https://app.emporiaresearch.com/survey/terminated?uid={{uid}}",
    }),
    participantIdParam: "uid",
    prefilledEmail: "charm@emporiaapp.com",
    isRejectReasonsExportOnByDefault: true,
  },
  {
    key: "gmo",
    name: "GMO",
    src: "g",
    emailDomain: "gmo-research.ai",
    extraUrlParams: () => ({
      redirectUrl:
        "https://infopanel.jp/lpark/enqInfoUpdateForNC.do?status=1&id={{iid}}",
      screenoutUrl:
        "https://infopanel.jp/lpark/enqInfoUpdateForNC.do?status=2&id={{iid}}",
    }),
    participantIdParam: "iid",
  },
  {
    key: "synquery",
    name: "SynQuery",
    src: "sq",
    emailDomain: "synquery.ai",
    extraUrlParams: genericRedirectExtraUrlParams,
    participantIdParam: "iid",
    inputFields: GENERIC_REDIRECT_INPUT_FIELDS,
  },
  {
    key: "grayscale",
    name: "Grayscale",
    src: "gs",
    emailDomain: "grayscalemr.com",
    extraUrlParams: genericRedirectExtraUrlParams,
    participantIdParam: "iid",
    inputFields: GENERIC_REDIRECT_INPUT_FIELDS,
    prefilledEmail: "karen.tsai@grayscalemr.com",
  },
  {
    key: "catalyse",
    name: "Catalyse",
    src: "cat",
    emailDomain: "catalyseresearch.com",
    extraUrlParams: () => ({
      redirectUrl: "https://uf.sampleeye.com/end?RT={{iid}}&Status=5",
      screenoutUrl: "https://uf.sampleeye.com/end?RT={{iid}}&Status=6",
    }),
    participantIdParam: "iid",
    prefilledEmail: "listenrfq@catalyseresearch.com",
  },
  {
    key: "exact_insight",
    name: "Exact Insight",
    src: "exact",
    emailDomain: "exactinsight.ai",
    extraUrlParams: (inputs): Record<string, string> => {
      const projectCode = inputs.projectCode?.trim();
      if (!projectCode) return {};
      const project = encodeURIComponent(projectCode);
      return {
        redirectUrl: `https://www.exactinsight.ai/survey/callback?project=${project}&status=complete&pid={{iid}}`,
        screenoutUrl: `https://www.exactinsight.ai/survey/callback?project=${project}&status=terminate&pid={{iid}}`,
      };
    },
    participantIdParam: "iid",
    inputFields: [
      {
        title: "Project Code",
        id: "projectCode",
        comment: "(Required)",
        required: true,
        longDescription:
          "Enter the 6-character uppercase alphanumeric project code provided by Exact Insight, e.g. R23N29.",
      },
    ],
    prefilledEmail: "will.carley@exactinsight.ai",
  },
  {
    key: "octopus",
    name: "Octopus",
    src: "oct",
    emailDomain: "octopusgroup.com.au",
    extraUrlParams: (inputs): Record<string, string> => {
      const surveyId = inputs.surveyId?.trim();
      if (!surveyId) return {};
      const survey = encodeURIComponent(surveyId);
      return {
        redirectUrl: `https://my.octopusgroup.com.au/return/postsurvey?SurveyId=${survey}&LinkSurveyId={{iid}}&Status=21`,
        screenoutUrl: `https://my.octopusgroup.com.au/return/postsurvey?SurveyId=${survey}&LinkSurveyId={{iid}}&Status=5`,
      };
    },
    participantIdParam: "iid",
    inputFields: [
      {
        title: "Survey ID",
        id: "surveyId",
        comment: "(Required)",
        required: true,
        longDescription: "Enter the full survey UUID provided by Octopus.",
      },
    ],
    prefilledEmail: "sales@octopusgroup.com.au",
  },
  {
    key: "dynata",
    name: "Dynata",
    src: "dyn",
    emailDomain: "dynata.com",
    extraUrlParams: () => ({
      redirectUrl:
        "https://dkr1.ssisurveys.com/projects/end?rst=1&psid={{iid}}",
      screenoutUrl:
        "https://dkr1.ssisurveys.com/projects/end?rst=2&psid={{iid}}",
    }),
    participantIdParam: "iid",
  },
  {
    key: "research_network",
    name: "Research Network",
    src: "rn",
    emailDomain: "research-network.co",
    isCreatable: false,
  },
  {
    key: "m3",
    name: "M3",
    src: "m3",
    emailDomain: "m3global.com",
    extraUrlParams: (inputs): Record<string, string> => {
      const projectId = inputs.projectId?.trim();
      if (!projectId) return {};
      const surveyKey = encodeURIComponent(projectId);
      return {
        redirectUrl: `https://hub.m3globalresearch.com/survey_complete.cfm?survey_key=${surveyKey}&user_id={{iid}}`,
        screenoutUrl: `https://hub.m3globalresearch.com/survey_unqual.cfm?survey_key=${surveyKey}&user_id={{iid}}`,
      };
    },
    participantIdParam: "iid",
    inputFields: [
      {
        title: "Project ID",
        id: "projectId",
        comment: "(Required)",
        required: true,
        longDescription:
          "Enter the project ID provided by M3 (their survey_key), e.g. SAMPLE123.",
      },
    ],
    prefilledEmail: "tjoyce@m3global.com",
  },
  {
    key: "konovo",
    name: "Konovo",
    src: "ko",
    emailDomain: "konovo.com",
    extraUrlParams: (inputs): Record<string, string> => {
      const projectId = inputs.projectId?.trim();
      if (!projectId) return {};
      const project = encodeURIComponent(projectId);
      return {
        redirectUrl: `https://surveys.opinionsite.com/survey/selfserve/20dc/${project}?shghash={{iid}}&t=1`,
        screenoutUrl: `https://surveys.opinionsite.com/survey/selfserve/20dc/${project}?shghash={{iid}}&t=2`,
      };
    },
    participantIdParam: "iid",
    inputFields: [
      {
        title: "Project ID",
        id: "projectId",
        comment: "(Required)",
        required: true,
        longDescription:
          "Enter the project ID provided by Konovo, e.g. SAMPLE123.",
      },
    ],
    prefilledEmail: "ellie.clifford@konovo.com",
  },
  {
    key: "market_xcel",
    name: "Market Xcel",
    src: "xcel",
    emailDomain: "market-xcel.com",
    extraUrlParams: () => ({
      redirectUrl:
        "https://cluster.marketxcel.co.in/survey/thankyou?auth=1&uid={{iid}}",
      screenoutUrl:
        "https://cluster.marketxcel.co.in/survey/thankyou?auth=2&uid={{iid}}",
    }),
    participantIdParam: "iid",
  },
  {
    key: "embrain",
    name: "Embrain",
    src: "emb",
    emailDomain: "embrain.com",
    extraUrlParams: (inputs): Record<string, string> => {
      const projectId = inputs.projectId?.trim();
      if (!projectId) return {};
      const alias = encodeURIComponent(projectId);
      return {
        redirectUrl: `https://survey.panel.co.kr/Foreign/nPanelSupply_End.asp?Alias=${alias}&cid={{iid}}&status=001`,
        screenoutUrl: `https://survey.panel.co.kr/Foreign/nPanelSupply_End.asp?Alias=${alias}&cid={{iid}}&status=003`,
      };
    },
    participantIdParam: "iid",
    inputFields: [
      {
        title: "ProjectID",
        id: "projectId",
        comment: "(Required)",
        required: true,
        longDescription: "Enter the ProjectID provided by Embrain.",
      },
    ],
  },
  {
    key: "paas",
    name: "Paas",
    src: "p",
    emailDomain: "paasresearch.com",
    extraUrlParams: () => ({
      redirectUrl:
        "https://www.humainion.com/endsurvey?status_id=6&response=1078&pid={{iid}}",
      screenoutUrl:
        "https://www.humainion.com/endsurvey?status_id=3&response=914&pid={{iid}}",
    }),
    participantIdParam: "iid",
  },
  {
    key: "woozle",
    name: "Woozle",
    src: "wz",
    emailDomain: "woozleresearch.com",
    extraUrlParams: () => ({
      redirectUrl:
        "https://app.woozleresearch.com/redirect?status=complete&wid={{iid}}",
      screenoutUrl:
        "https://app.woozleresearch.com/redirect?status=screened&wid={{iid}}",
    }),
    participantIdParam: "iid",
  },
  {
    key: "thinknow",
    name: "ThinkNow",
    src: "now",
    emailDomain: "thinknow.com",
    extraUrlParams: () => ({
      redirectUrl:
        "https://notch.insights.supply/cb?token=cde7b11c-cdce-4f90-a511-9c1e11ccb68a&RID={{iid}}",
      screenoutUrl:
        "https://samplicio.us/s/ClientCallBack.aspx?RIS=20&RID={{iid}}",
    }),
    participantIdParam: "iid",
  },
  {
    key: "paloma",
    name: "Paloma",
    src: "plm",
    emailDomain: "palomasolutions.com",
  },
  {
    key: "equilibrium",
    name: "Equilibrium",
    src: "eq",
    emailDomain: "equilibriumbdc.com",
  },
  {
    key: "newfield",
    name: "Newfield",
    src: "nf",
    emailDomain: "newfield-research.com",
  },
];

export const EXTERNAL_PANEL_SRC_SET = new Set<string>(
  EXTERNAL_PANELS.map((panel) => panel.src)
);

// The modal either configures a static vendor panel or a user-defined custom
// panel (persisted per-dashboard in metadata.customPanel). `name` is only set
// in send mode, taken from the dashboard row.
export type ExternalPanelModalTarget =
  | { kind: "vendor"; panel: ExternalPanel }
  | { kind: "custom"; name?: string };

// Keeps user-entered src / participant-id params clean inside ?key=value links.
export const CUSTOM_SRC_REGEX = /^[A-Za-z0-9_-]+$/;

// Params the study link machinery already interprets; "cr__listen" mirrors
// CUSTOM_RECRUIT_PARAM (not imported: panel/utils imports this file).
// Includes the respondent-control params read by the participant entry routes:
// setting any of them on a launch link changes what participants get (e.g.
// preview bounces anonymous respondents to sign-in and stops counting
// responses).
export const RESERVED_LINK_PARAM_NAMES = new Set<string>([
  EXTERNAL_PANEL_URL_PARAM_NAME,
  "lang",
  "language",
  "lng",
  "redirectUrl",
  "screenoutUrl",
  "_sampled",
  "cr__listen",
  "preview",
  "restart",
  // its presence is what marks an arrival as a panel's
  PANEL_API_INTERVIEW_PARAM,
]);

// Shared by the creation modal and the create-external route so client and
// server enforce identical rules. A custom src must not collide with a vendor
// src: getExternalPanelBySrc would resolve the dashboard as that vendor
// everywhere.
export const getCustomSrcValidationError = (src: string): string | null => {
  if (!src) return "Source value is required";
  if (!CUSTOM_SRC_REGEX.test(src)) {
    return "Use only letters, numbers, hyphens, and underscores";
  }
  const vendor = getExternalPanelBySrc(src);
  if (vendor) return `"${src}" is reserved for ${vendor.name}`;
  return null;
};

export const getParticipantIdParamValidationError = (
  param: string
): string | null => {
  if (!param) return null;
  if (!CUSTOM_SRC_REGEX.test(param)) {
    return "Use only letters, numbers, hyphens, and underscores";
  }
  if (RESERVED_LINK_PARAM_NAMES.has(param)) {
    return `"${param}" is a reserved parameter name`;
  }
  return null;
};

// A draft row has only `metadata.draft`; it is promoted in place on finalize.
export const isDraftDashboard = (row: {
  metadata: RecruitmentDashboardMetadata;
}): boolean => notNull(row.metadata?.draft);

export const getExternalPanelBySrc = (
  src: string | null | undefined
): ExternalPanel | undefined => {
  if (!src) return undefined;
  return EXTERNAL_PANELS.find((panel) => panel.src === src);
};

// Custom-link only: a src can be shared across dashboards, cr__listen cannot.
export const isRejectReasonsExportEnabled = ({
  src,
  recruitmentId,
  override,
}: {
  src: string | null | undefined;
  recruitmentId: string | null;
  override: boolean | undefined;
}): boolean =>
  notNull(recruitmentId) &&
  (override ??
    getExternalPanelBySrc(src)?.isRejectReasonsExportOnByDefault ??
    false);

export const getExternalPanelByKey = (
  key: string | null | undefined
): ExternalPanel | undefined => {
  if (!key) return undefined;
  return EXTERNAL_PANELS.find((panel) => panel.key === key);
};
